import type { ExperienceJSON, ElementState, ElementKeyframe } from '../types';

/**
 * Migrate legacy visibilityTriggers to states
 * Note: textKeyframes are now deprecated - use element.keyframes instead
 */
export function migrateToStates(experience: ExperienceJSON): ExperienceJSON {
  const migrated = { ...experience };

  for (const [layoutId, layout] of Object.entries(migrated.layouts)) {
    migrated.layouts[layoutId] = {
      ...layout,
      elements: layout.elements.map(el => {
        const states: ElementState[] = el.states ? [...el.states] : [];
        const keyframes: { [trackId: number]: ElementKeyframe[] } = el.keyframes ? { ...el.keyframes } : {};

        // Migrate legacy textKeyframes to per-element keyframes
        if ('textKeyframes' in el && el.textKeyframes && el.textKeyframes.length > 0) {
          for (const kf of el.textKeyframes) {
            const trackId = kf.trackId;
            if (!keyframes[trackId]) {
              keyframes[trackId] = [];
            }
            // Note: keyframeIndex refers to old global keyframe times which we don't have anymore
            // For migration, we'll use the index as a placeholder time (in seconds)
            // Real migration would need the original timeline keyframe times
            keyframes[trackId].push({
              time: kf.keyframeIndex, // Placeholder - real migration needs original times
              text: kf.text,
              style: kf.style,
            });
          }
        }

        // Migrate visibilityTriggers to variable-based states
        if (el.visibilityTriggers && el.visibilityTriggers.length > 0) {
          const hasVisibilityState = states.some(s => s.id === 'migrated-visibility');
          if (!hasVisibilityState) {
            states.push({
              id: 'migrated-visibility',
              conditions: el.visibilityTriggers.map(t => ({
                variable: t.variableName,
                value: t.value,
              })),
              style: { visible: true },
            });
          }
        }

        // Return element with migrated data
        const hasKeyframes = Object.keys(keyframes).length > 0;
        const hasStates = states.length > 0;

        if (!hasKeyframes && !hasStates) return el;

        return {
          ...el,
          ...(hasKeyframes ? { keyframes } : {}),
          ...(hasStates ? { states } : {}),
        };
      }),
    };
  }

  return migrated;
}
